import { useTranslation } from "react-i18next";
import { Avatar } from "@/atoms/avatar";
import { BrandBar } from "@/components/brand-bar";
import { Button } from "@/atoms/button";
import { StripeLine } from "@/components/stripe-line";
import { CancelModal } from "./cancel-modal";
import { InvoicesSection } from "./invoices-section";
import { PaymentMethodsSection } from "./payment-methods-section";
import { SubscriptionsSection } from "./subscriptions-section";
import { coachLabel } from "@/utils/coach";
import type {
  PortalData,
  PortalInvoice,
  PortalSubscription,
} from "@/data/portal";
import type { Stripe } from "@stripe/stripe-js";
import type { ReactNode } from "react";

/**
 * The signed-in billing page (template detail 1.7; the shell is n/a, this app has no rail).
 *
 * One column at 640 on paper. The identity block first, on paper with a hairline under it and
 * never as a card (detail 1.7 doNot 1), then three sections in the order a person reads them:
 * what you pay for, how you pay, what you paid. Each section is an h2 at 20 with a hairline
 * between sections and nothing else separating them.
 *
 * The brand-coloured header band, the six hand-picked status hexes, the 10 px badge text, the
 * blurred scrim, the red glow under Cancel, the hover lifts, the dashed empty boxes and the
 * "Powered by" lockup are all gone. Nothing on this page is the coach's colour.
 *
 * It takes its data as props and nothing else, so `/dev/portal` can render every state of it
 * without a token and without a network.
 */

export interface PortalViewProps {
  data: PortalData;
  invoices: PortalInvoice[];
  stripe: Stripe | null;
  /** The resolved coach colour, so the setup Elements remount when it changes. */
  coachHex?: string | null;
  language: string;
  /* Payment methods */
  addingCard: boolean;
  setupClientSecret: string | null;
  settingDefaultId: string | null;
  cardsError: string | null;
  onOpenAdd: () => void;
  onCloseAdd: () => void;
  onCardAdded: () => void;
  onSetDefault: (paymentMethodId: string) => void;
  /* Cancelling */
  cancelTarget: PortalSubscription | null;
  cancelling: boolean;
  cancelError: string | null;
  onAskCancel: (subscription: PortalSubscription) => void;
  onCloseCancel: () => void;
  onConfirmCancel: (subscription: PortalSubscription) => void;
}

export function PortalView(props: PortalViewProps) {
  const { t } = useTranslation();
  const { data, invoices } = props;
  const coachName = coachLabel(data.business.name, t);
  const supportEmail = data.business.supportEmail;
  const customerName = data.customer.name?.trim();
  const email = data.customer.email?.trim() ?? "";

  return (
    <div className="bg-surface-paper flex min-h-screen justify-center">
      <div className="px-gk-4 sm:px-gk-6 py-gk-4 sm:py-gk-8 gap-gk-6 flex w-full max-w-[640px] flex-col">
        <div className="gap-gk-4 sm:gap-gk-6 flex flex-col">
          <BrandBar />
          <div className="ds-reveal ds-hairline gap-gk-4 pb-gk-6 flex items-center border-b">
            <Avatar
              name={customerName || email}
              src={data.customer.photo}
              size={48}
            />
            <div className="gap-gk-1 flex min-w-0 flex-col">
              <h1 className="text-[24px] leading-[1.2] font-semibold break-words text-[var(--gk-text-primary)]">
                {customerName || email}
              </h1>
              {/* The facts line wraps rather than truncating: at 390 it held the email and cut
                  the coach's name off mid-word, and the coach's name is the one thing on this page
                  that says whose billing it is. */}
              <p className="text-[14px] break-words text-[var(--gk-text-secondary)]">
                {t("gymkeePay.portal.facts", { email, coach: coachName })}
              </p>
            </div>
          </div>
        </div>

        <main className="flex flex-col">
          <Section title={t("gymkeePay.portal.subscriptions")}>
            <SubscriptionsSection
              subscriptions={data.subscriptions}
              coachName={coachName}
              supportEmail={supportEmail}
              onCancel={props.onAskCancel}
            />
          </Section>

          <Section
            title={t("gymkeePay.portal.paymentMethods")}
            action={
              !props.addingCard && data.paymentMethods.length > 0 ? (
                <Button
                  variant="secondary"
                  className="ds-pay-hand"
                  onClick={props.onOpenAdd}
                >
                  {t("gymkeePay.portal.addACard")}
                </Button>
              ) : undefined
            }
          >
            <PaymentMethodsSection
              methods={data.paymentMethods}
              settingDefaultId={props.settingDefaultId}
              onSetDefault={props.onSetDefault}
              adding={props.addingCard}
              setupClientSecret={props.setupClientSecret}
              stripe={props.stripe}
              coachHex={props.coachHex}
              language={props.language}
              onOpenAdd={props.onOpenAdd}
              onCloseAdd={props.onCloseAdd}
              onAdded={props.onCardAdded}
              error={props.cardsError}
            />
          </Section>

          <Section title={t("gymkeePay.portal.invoicesSection")}>
            <InvoicesSection invoices={invoices} />
          </Section>
        </main>

        <footer className="gap-gk-1 pb-gk-8 flex flex-col">
          {supportEmail && (
            <p className="text-[13px] text-[var(--gk-text-tertiary)]">
              {t("gymkeePay.common.questions")}{" "}
              <a
                href={`mailto:${supportEmail}`}
                className="underline"
                style={{ color: "var(--gk-text-secondary)" }}
              >
                {t("gymkeePay.common.messageCoach", { coach: coachName })}
              </a>
            </p>
          )}
          <StripeLine />
        </footer>
      </div>

      <CancelModal
        subscription={props.cancelTarget}
        coachName={coachName}
        cancelling={props.cancelling}
        error={props.cancelError}
        onClose={props.onCloseCancel}
        onConfirm={props.onConfirmCancel}
      />
    </div>
  );
}

/** A section: the heading with 32 above and 16 below, and a hairline between sections. */
function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ds-hairline pt-gk-8 pb-gk-8 flex flex-col border-b last:border-b-0">
      <div className="mb-gk-4 gap-gk-4 flex min-h-[40px] items-center justify-between">
        <h2 className="text-[20px] font-semibold text-[var(--gk-text-primary)]">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}
